/**
 * ScholarSync — Checkout Page
 * File: frontend/src/Checkout.js
 * 
 * Plan selection → Stripe Checkout redirect.
 * Parent picks Individual ($47/yr) or Family ($97/yr), clicks Subscribe,
 * and is redirected to Stripe's hosted checkout page.
 */

import React, { useState } from 'react';
import { API_URL } from './config';

export default function Checkout() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('ss_token');
      const res = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planType: selectedPlan }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to start checkout');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: 800, width: '100%' }}>
        <h1 style={{ textAlign: 'center', marginBottom: 8, color: '#1a1a2e' }}>
          Choose Your Plan
        </h1>
        <p style={{ textAlign: 'center', color: '#555', marginBottom: 40, fontSize: 16 }}>
          Unlock AI-powered scholarship matching for your student
        </p>

        <div style={{
          display: 'flex',
          gap: 24,
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Individual Plan */}
          <div
            onClick={() => setSelectedPlan('individual')}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '32px 28px',
              width: 320,
              cursor: 'pointer',
              border: selectedPlan === 'individual' ? '3px solid #4a6cf7' : '3px solid transparent',
              boxShadow: selectedPlan === 'individual'
                ? '0 8px 30px rgba(74, 108, 247, 0.2)'
                : '0 4px 15px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease'
            }}
          >
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e' }}>Individual</h2>
            <div style={{ fontSize: 36, fontWeight: 700, color: '#4a6cf7', marginBottom: 4 }}>
              $47
              <span style={{ fontSize: 16, fontWeight: 400, color: '#888' }}>/year</span>
            </div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
              Perfect for one student
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'AI-powered scholarship matching',
                'Explainability scores for every match',
                'Application tracking dashboard',
                'Deadline alerts & filters',
                '1 student profile'
              ].map((feature, i) => (
                <li key={i} style={{
                  padding: '6px 0',
                  fontSize: 14,
                  color: '#444',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ color: '#4a6cf7' }}>✓</span> {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Family Plan */}
          <div
            onClick={() => setSelectedPlan('family')}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '32px 28px',
              width: 320,
              cursor: 'pointer',
              border: selectedPlan === 'family' ? '3px solid #10b981' : '3px solid transparent',
              boxShadow: selectedPlan === 'family'
                ? '0 8px 30px rgba(16, 185, 129, 0.2)'
                : '0 4px 15px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            <div style={{
              position: 'absolute',
              top: -12,
              right: 20,
              background: '#10b981',
              color: '#fff',
              padding: '4px 14px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600
            }}>
              BEST VALUE
            </div>
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e' }}>Family</h2>
            <div style={{ fontSize: 36, fontWeight: 700, color: '#10b981', marginBottom: 4 }}>
              $97
              <span style={{ fontSize: 16, fontWeight: 400, color: '#888' }}>/year</span>
            </div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
              Multiple students, one account
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Everything in Individual, plus:',
                'Unlimited student profiles',
                'Parent dashboard for all children',
                'Family-wide scholarship tracking',
                'Best value for families with 2+ students'
              ].map((feature, i) => (
                <li key={i} style={{
                  padding: '6px 0',
                  fontSize: 14,
                  color: '#444',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ color: '#10b981' }}>✓</span> {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Subscribe Button */}
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          {error && (
            <p style={{ color: '#e53e3e', marginBottom: 16, fontSize: 14 }}>{error}</p>
          )}
          <button
            onClick={handleCheckout}
            disabled={!selectedPlan || loading}
            style={{
              background: selectedPlan
                ? (selectedPlan === 'individual' ? '#4a6cf7' : '#10b981')
                : '#ccc',
              color: '#fff',
              border: 'none',
              padding: '14px 48px',
              borderRadius: 8,
              fontSize: 18,
              fontWeight: 600,
              cursor: selectedPlan && !loading ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Redirecting to checkout...' : 
             selectedPlan ? `Subscribe — $${selectedPlan === 'individual' ? '47' : '97'}/year` :
             'Select a plan'}
          </button>
          <p style={{ color: '#888', fontSize: 13, marginTop: 12 }}>
            Secure checkout powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
